import { Box, Typography } from '@mui/material';
import { XIcon } from 'lucide-react';
import { Fragment } from 'react';
import { SsbCondition } from '../../types/SimilarlySituated/SsbCondition.type';
import { normalizeText } from '../../utils/normalizeText.ts';
import { conditionCode, conditionText } from '../../utils/ssbCondition';
import { CONDITION_COLORS, ConditionVariant } from '../../utils/ssbConditionColors';
import { BUTTON_RESET } from '../../utils/ssbStyles';

interface ConditionChipProps {
  condition: SsbCondition;
  variant: ConditionVariant;
  onEdit: () => void;
  onRemove: () => void;
}

function ConditionChip({ condition, variant, onEdit, onRemove }: ConditionChipProps) {
  const colors = CONDITION_COLORS[variant];
  const code = conditionCode(condition);
  const statutes = condition.statutes ?? [];
  const pairs =
    statutes.length > 0
      ? statutes.map((s) => ({ key: s.id, code: s.display_name, text: normalizeText(s.description) }))
      : [{ key: code, code, text: conditionText(condition) }];

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'stretch',
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.background,
        borderRadius: '24px',
        maxWidth: '100%',
        overflow: 'hidden',
        color: colors.accent,
        '&:hover': { backgroundColor: colors.hoverBackground },
      }}
    >
      <Box
        component='button'
        type='button'
        onClick={onEdit}
        title='Edit this condition'
        sx={{ ...BUTTON_RESET, flexWrap: 'wrap', gap: '4px 8px', padding: '6px 10px 6px 12px', minWidth: 0 }}
      >
        {pairs.map((pair, index) => (
          <Fragment key={pair.key}>
            {index > 0 && <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#718096' }}>OR</Typography>}
            <Typography sx={{ fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }}>{pair.code}</Typography>
            {pair.text && <Typography sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{pair.text}</Typography>}
          </Fragment>
        ))}
      </Box>

      <Box
        component='button'
        type='button'
        onClick={onRemove}
        aria-label={`Remove ${code}`}
        sx={{ ...BUTTON_RESET, padding: '6px 12px 6px 10px', '&:hover': { opacity: 0.7 } }}
      >
        <XIcon size={14} />
      </Box>
    </Box>
  );
}

export default ConditionChip;
